import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import FilterBar from "../components/FilterBar";
import VAMap from "../components/VAMap";
import EventCard from "../components/EventCard";
import {
    fetchMeta,
    fetchEvents,
    fetchCountySummary,
} from "../lib/api";
import { X, Link2 } from "lucide-react";
import ContactSection from "../components/ContactSection";

const DEFAULT_FILTERS = {
    season: "All",
    gender: "All",
    age_range: "All",
    event_type: "All",
};

const FILTER_KEYS = ["season", "gender", "age_range", "event_type"];

function filtersFromSearch(sp) {
    const out = { ...DEFAULT_FILTERS };
    FILTER_KEYS.forEach((k) => {
        const v = sp.get(k);
        if (v) out[k] = v;
    });
    return out;
}

export default function HomePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [meta, setMeta] = useState(null);
    const [filters, setFiltersRaw] = useState(() =>
        filtersFromSearch(searchParams),
    );
    const [events, setEvents] = useState([]);
    const [summary, setSummary] = useState({});
    const [selectedCounty, setSelectedCountyRaw] = useState(
        () => searchParams.get("county") || null,
    );
    const [loading, setLoading] = useState(false);

    // Sync state -> URL
    useEffect(() => {
        const params = {};
        FILTER_KEYS.forEach((k) => {
            if (filters[k] && filters[k] !== "All") params[k] = filters[k];
        });
        if (selectedCounty) params.county = selectedCounty;
        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, selectedCounty]);

    // Wrapper that accepts a function or object (same API as useState)
    const setFilters = useCallback((updater) => {
        setFiltersRaw((prev) =>
            typeof updater === "function" ? updater(prev) : updater,
        );
    }, []);
    const setSelectedCounty = useCallback((v) => setSelectedCountyRaw(v), []);

    useEffect(() => {
        fetchMeta().then(setMeta);
    }, []);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            toast.success("Link copied to clipboard");
        } catch {
            toast.error("Could not copy link");
        }
    };

    const params = useMemo(() => {
        const p = { ...filters };
        if (selectedCounty) p.county = selectedCounty;
        return p;
    }, [filters, selectedCounty]);

    const reload = async () => {
        setLoading(true);
        try {
            const [ev, sm] = await Promise.all([
                fetchEvents(params),
                fetchCountySummary(filters),
            ]);
            setEvents(ev);
            setSummary(sm);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, selectedCounty]);

    const totalEventsVisible = events.length;
    const countiesWithEvents = Object.keys(summary).length;

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-white font-manrope relative"
            data-testid="home-page"
        >
            <div className="grain-overlay" />

            {/* Header */}
            <header
                className="sticky top-0 z-40 backdrop-blur-2xl bg-black/70 border-b border-white/10"
                data-testid="site-header"
            >
                <div className="px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link
                        to="/"
                        className="flex items-center gap-3"
                        data-testid="brand-link"
                    >
                        <img
                            src="/va-lax-hq-logo.png"
                            alt="VA Lax HQ — Virginia Lacrosse Headquarters"
                            className="h-16 sm:h-20 w-auto object-contain"
                            data-testid="site-logo"
                        />
                        <div className="hidden md:block">
                            <div className="font-anton text-xl tracking-widest leading-none">
                                VA LAX HQ
                            </div>
                            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mt-1">
                                Virginia Lacrosse Events
                            </div>
                        </div>
                    </Link>
                    <nav className="flex items-center gap-2">
                        <Link
                            to="/submit"
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2 bg-[#FF3B30] hover:bg-[#FF574D] text-white transition-colors"
                            data-testid="submit-cta"
                        >
                            Submit Event · Free
                        </Link>
                        <button
                            onClick={copyLink}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                            data-testid="copy-link-btn"
                            title="Copy shareable link with current filters"
                        >
                            <Link2 size={13} /> Share
                        </button>
                        <Link
                            to="/admin"
                            className="text-xs uppercase tracking-[0.22em] font-semibold px-4 py-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                            data-testid="admin-link"
                        >
                            Admin
                        </Link>
                    </nav>
                </div>
            </header>

            <main className="px-6 md:px-10 pt-8 pb-24 relative z-10">
                {/* Hero title */}
                <section className="mb-10 max-w-5xl">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-semibold mb-4">
                        Statewide · Season 2026
                    </div>
                    <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-white uppercase">
                        Every stick.{" "}
                        <span className="text-[#FF3B30]">Every county.</span>
                        <br />
                        One map of Virginia lacrosse.
                    </h1>
                    <p className="mt-5 text-sm sm:text-base text-zinc-400 max-w-2xl leading-relaxed">
                        Filter leagues, tournaments, camps, and clinics across
                        all {" "}
                        <span className="text-white">134 counties &amp;
                        independent cities</span> . Click a county on the map
                        to zero in on local events.
                    </p>

                    <div className="flex gap-6 mt-6">
                        <Stat
                            label="Events"
                            value={totalEventsVisible}
                            testId="stat-events"
                        />
                        <Divider />
                        <Stat
                            label="Active Counties"
                            value={countiesWithEvents}
                            testId="stat-counties"
                        />
                        <Divider />
                        <Stat
                            label="Filters"
                            value={
                                Object.values(filters).filter(
                                    (v) => v !== "All",
                                ).length + (selectedCounty ? 1 : 0)
                            }
                            testId="stat-filters"
                        />
                    </div>
                </section>

                {/* Filters */}
                <section className="mb-8">
                    <FilterBar
                        filters={filters}
                        setFilters={setFilters}
                        meta={meta}
                    />
                </section>

                {/* Grid: Map + Events */}
                <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Map */}
                    <div
                        className="lg:col-span-8 border border-white/10 bg-[#0d0d0d] p-4 sm:p-6 relative overflow-hidden"
                        data-testid="map-panel"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                                    Commonwealth of Virginia
                                </div>
                                <h2 className="font-anton text-2xl sm:text-3xl uppercase mt-1">
                                    Click a county
                                </h2>
                            </div>
                            {selectedCounty && (
                                <button
                                    onClick={() => setSelectedCounty(null)}
                                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#FF3B30] hover:text-white border border-[#FF3B30]/50 hover:border-white px-3 py-1.5"
                                    data-testid="clear-county"
                                >
                                    <X size={12} /> Clear
                                </button>
                            )}
                        </div>
                        <VAMap
                            summary={summary}
                            selected={selectedCounty}
                            onSelect={setSelectedCounty}
                        />
                    </div>

                    {/* Events */}
                    <aside
                        className="lg:col-span-4 flex flex-col gap-3"
                        data-testid="events-panel"
                    >
                        <div className="flex items-baseline justify-between pb-3 border-b border-white/10">
                            <h2 className="font-anton text-2xl uppercase">
                                {selectedCounty || "All Events"}
                            </h2>
                            <span className="text-xs tracking-[0.2em] uppercase text-zinc-500">
                                {events.length} result
                                {events.length === 1 ? "" : "s"}
                            </span>
                        </div>

                        <div
                            className="flex flex-col gap-3 max-h-[72vh] overflow-y-auto custom-scrollbar pr-1"
                            data-testid="events-list"
                        >
                            {loading && events.length === 0 ? (
                                <Placeholder text="Loading events…" />
                            ) : events.length === 0 ? (
                                <EmptyState />
                            ) : (
                                events.map((e, i) => (
                                    <EventCard
                                        key={e.id}
                                        event={e}
                                        index={i}
                                    />
                                ))
                            )}
                        </div>
                    </aside>
                </section>
            </main>

            <ContactSection />

            <footer className="relative z-10 border-t border-white/10 px-6 md:px-10 py-6 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                <span>VA LAX HQ · 2026</span>
                <span>Built for the Commonwealth</span>
            </footer>
        </div>
    );
}

const Stat = ({ label, value, testId }) => (
    <div data-testid={testId}>
        <div className="font-anton text-3xl text-white">{value}</div>
        <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mt-0.5">
            {label}
        </div>
    </div>
);

const Divider = () => <div className="w-px h-10 bg-white/10 self-center" />;

const Placeholder = ({ text }) => (
    <div className="border border-dashed border-white/10 p-6 text-center text-xs tracking-[0.2em] uppercase text-zinc-500">
        {text}
    </div>
);

const EmptyState = () => (
    <div
        className="border border-dashed border-white/10 p-6 text-center"
        data-testid="empty-state"
    >
        <div className="font-anton text-2xl uppercase text-white mb-2">
            No events yet
        </div>
        <p className="text-xs text-zinc-500">
            Try clearing filters, or ask organizers to list their events on
            the <span className="text-white">Submit Event</span> page.
        </p>
    </div>
);
